import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PiggyBank,
  Plus,
  Trash2,
  Edit2,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Copy,
  Bell,
  BellOff,
  ChevronLeft,
  ChevronRight,
  Palette,
  Link2,
  Target,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
  Mail,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PRESET_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#ef4444", "#f97316", 
  "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6"
];

export const Budget = () => {
  const [activeTab, setActiveTab] = useState("overview");

  // Airtime Machine
  const [airtimeBalance, setAirtimeBalance] = useState(null);
  const [airtimeLoading, setAirtimeLoading] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupDate, setTopupDate] = useState(new Date().toISOString().slice(0, 10));
  const [topupNote, setTopupNote] = useState("");
  const [addingTopup, setAddingTopup] = useState(false);
  const [airtimeSettings, setAirtimeSettings] = useState({ warning_threshold: 500, critical_threshold: 100, alerts_enabled: true });
  const [savingAirtimeSettings, setSavingAirtimeSettings] = useState(false);
  
  // Airtime Balance Adjustment
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentDate, setAdjustmentDate] = useState(new Date().toISOString().slice(0, 10));
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [addingAdjustment, setAddingAdjustment] = useState(false);

  const fetchAirtimeBalance = async () => {
    setAirtimeLoading(true);
    try {
      const res = await axios.get(`${API}/airtime-machine/balance`);
      setAirtimeBalance(res.data);
    } catch (e) {
      toast.error("Failed to load airtime machine balance");
    } finally {
      setAirtimeLoading(false);
    }
  };

  const fetchAirtimeSettings = async () => {
    try {
      const res = await axios.get(`${API}/airtime-machine/settings`);
      setAirtimeSettings(res.data);
    } catch (e) {}
  };

  const saveAirtimeSettings = async () => {
    setSavingAirtimeSettings(true);
    try {
      await axios.put(`${API}/airtime-machine/settings`, airtimeSettings);
      toast.success("Alert thresholds saved");
      fetchAirtimeBalance();
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSavingAirtimeSettings(false);
    }
  };

  const handleAddTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setAddingTopup(true);
    try {
      await axios.post(`${API}/airtime-machine/topup`, {
        amount: parseFloat(topupAmount),
        date: topupDate,
        note: topupNote,
      });
      toast.success("Top-up recorded");
      setTopupAmount("");
      setTopupNote("");
      fetchAirtimeBalance();
    } catch (e) {
      toast.error("Failed to record top-up");
    } finally {
      setAddingTopup(false);
    }
  };

  const handleDeleteTopup = async (id) => {
    try {
      await axios.delete(`${API}/airtime-machine/topup/${id}`);
      toast.success("Top-up removed");
      fetchAirtimeBalance();
    } catch (e) {
      toast.error("Failed to remove top-up");
    }
  };

  const handleAddAdjustment = async () => {
    if (!adjustmentAmount || parseFloat(adjustmentAmount) === 0) {
      toast.error("Please enter a valid amount (positive or negative)");
      return;
    }
    if (!adjustmentReason.trim()) {
      toast.error("Please enter a reason for the adjustment");
      return;
    }
    setAddingAdjustment(true);
    try {
      await axios.post(`${API}/airtime-machine/adjustment`, {
        amount: parseFloat(adjustmentAmount),
        date: adjustmentDate,
        reason: adjustmentReason,
      });
      toast.success("Adjustment recorded");
      setAdjustmentAmount("");
      setAdjustmentReason("");
      fetchAirtimeBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record adjustment");
    } finally {
      setAddingAdjustment(false);
    }
  };

  const handleDeleteAdjustment = async (id) => {
    try {
      await axios.delete(`${API}/airtime-machine/adjustment/${id}`);
      toast.success("Adjustment removed");
      fetchAirtimeBalance();
    } catch (e) {
      toast.error("Failed to remove adjustment");
    }
  };

  const [testingAlert, setTestingAlert] = useState(false);
  const handleTestAlert = async () => {
    setTestingAlert(true);
    try {
      const res = await axios.post(`${API}/airtime-machine/test-alert`);
      toast.success(`Test email sent to ${res.data.message?.split("sent to ")[1] || "your email"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test alert");
    } finally {
      setTestingAlert(false);
    }
  };

  useEffect(() => {
    if (activeTab === "airtime-machine" && !airtimeBalance) {
      fetchAirtimeBalance();
      fetchAirtimeSettings();
    }
  }, [activeTab]);
  
  // Date selection
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sales Targets Data
  const [salesTargetsSummary, setSalesTargetsSummary] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [showSalesTargetModal, setShowSalesTargetModal] = useState(false);
  const [editingSalesTarget, setEditingSalesTarget] = useState(null);
  const [salesTargetForm, setSalesTargetForm] = useState({
    category: "",
    revenue_target: "",
    profit_target: ""
  });
  
  // Projected Income Data
  const [projectedIncome, setProjectedIncome] = useState(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  
  // Form state
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    color: "#6366f1",
    expense_category_id: ""
  });
  const [budgetForm, setBudgetForm] = useState({
    category_id: "",
    budget_amount: ""
  });
  const [copyForm, setCopyForm] = useState({
    from_year: selectedYear,
    from_month: selectedMonth > 1 ? selectedMonth - 1 : 12,
    to_year: selectedYear,
    to_month: selectedMonth
  });

  useEffect(() => {
    loadData();
    loadSalesTargets();
    loadProjectedIncome();
  }, [selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, expCatRes, summaryRes, alertsRes] = await Promise.all([
        axios.get(`${API}/budgets/categories`),
        axios.get(`${API}/expenses/categories`),
        axios.get(`${API}/budgets/summary?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/budgets/alerts?unread_only=true`)
      ]);
      
      setCategories(catRes.data);
      setExpenseCategories(expCatRes.data);
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error("Error loading budget data:", error);
      toast.error("Failed to load budget data");
    } finally {
      setLoading(false);
    }
  };

  const loadProjectedIncome = async () => {
    setProjectionLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/projected-income?year=${selectedYear}&month=${selectedMonth}`);
      setProjectedIncome(res.data);
    } catch (error) {
      console.error("Error loading projected income:", error);
    } finally {
      setProjectionLoading(false);
    }
  };

  const loadSalesTargets = async () => {
    try {
      const [summaryRes, catRes] = await Promise.all([
        axios.get(`${API}/budgets/sales-targets/summary?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/categories-departments`)
      ]);
      setSalesTargetsSummary(summaryRes.data);
      setProductCategories(catRes.data.filter(c => c.type === 'category'));
    } catch (error) {
      console.error("Error loading sales targets:", error);
    }
  };

  const handleSaveSalesTarget = async () => {
    if (!salesTargetForm.category) {
      toast.error("Please select a category");
      return;
    }
    
    try {
      await saveWithRetry(() => axios.post(`${API}/budgets/sales-targets`, {
        category: salesTargetForm.category,
        category_type: "category",
        year: selectedYear,
        month: selectedMonth,
        revenue_target: parseFloat(salesTargetForm.revenue_target) || 0,
        profit_target: parseFloat(salesTargetForm.profit_target) || 0
      }));
      
      toast.success("Sales target saved");
      setShowSalesTargetModal(false);
      setSalesTargetForm({ category: "", revenue_target: "", profit_target: "" });
      setEditingSalesTarget(null);
      loadSalesTargets();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save sales target"));
    }
  };

  const handleDeleteSalesTarget = async (targetId) => {
    if (!confirm("Delete this sales target?")) return;
    
    try {
      await axios.delete(`${API}/budgets/sales-targets/${targetId}`);
      toast.success("Target deleted");
      loadSalesTargets();
    } catch (error) {
      toast.error("Failed to delete target");
    }
  };

  const checkAlerts = async () => {
    try {
      const res = await axios.post(`${API}/budgets/alerts/check?year=${selectedYear}&month=${selectedMonth}`);
      if (res.data.alerts_created > 0) {
        toast.warning(`${res.data.alerts_created} new budget alert(s) created!`);
        loadData();
      } else {
        toast.info("No new alerts");
      }
    } catch (error) {
      toast.error("Failed to check alerts");
    }
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    
    try {
      const payload = {
        ...categoryForm,
        expense_category_id: categoryForm.expense_category_id || null
      };
      
      if (editingCategory) {
        await saveWithRetry(() => axios.put(`${API}/budgets/categories/${editingCategory.id}`, payload));
        toast.success("Category updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/budgets/categories`, payload));
        toast.success("Category created");
      }
      
      setShowCategoryModal(false);
      resetCategoryForm();
      loadData();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save category"));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm("Delete this budget category?")) return;
    
    try {
      await saveWithRetry(() => axios.delete(`${API}/budgets/categories/${categoryId}`));
      toast.success("Category deleted");
      loadData();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to delete category"));
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: "", description: "", color: "#6366f1", expense_category_id: "" });
    setEditingCategory(null);
  };

  // Budget CRUD
  const handleSaveBudget = async () => {
    if (!budgetForm.category_id || !budgetForm.budget_amount) {
      toast.error("Please select a category and enter an amount");
      return;
    }
    
    try {
      await saveWithRetry(() => axios.post(`${API}/budgets/monthly`, {
        category_id: budgetForm.category_id,
        year: selectedYear,
        month: selectedMonth,
        budget_amount: parseFloat(budgetForm.budget_amount)
      }));
      
      toast.success("Budget saved");
      setShowBudgetModal(false);
      setBudgetForm({ category_id: "", budget_amount: "" });
      setEditingBudget(null);
      loadData();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save budget"));
    }
  };

  // Copy budgets
  const handleCopyBudgets = async () => {
    try {
      const res = await axios.post(
        `${API}/budgets/copy?from_year=${copyForm.from_year}&from_month=${copyForm.from_month}&to_year=${copyForm.to_year}&to_month=${copyForm.to_month}`
      );
      toast.success(res.data.message);
      setShowCopyModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy budgets");
    }
  };

  // Mark alert as read
  const markAlertRead = async (alertId) => {
    try {
      await axios.put(`${API}/budgets/alerts/${alertId}/read`);
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (error) {
      console.error("Error marking alert read:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "over": return "text-red-600 bg-red-50";
      case "warning": return "text-yellow-600 bg-yellow-50";
      default: return "text-green-600 bg-green-50";
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return "bg-red-500";
    if (percent >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <PiggyBank className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-3xl font-bold">Budget Management</h1>
            <p className="text-gray-500">Track and manage your monthly budgets</p>
          </div>
        </div>
        
        {/* Month Navigation */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-lg font-semibold min-w-[160px] text-center">
            {MONTHS[selectedMonth - 1]} {selectedYear}
          </div>
          <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="mb-6 border-yellow-300 bg-yellow-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">
                  {alerts.length} Budget Alert{alerts.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex gap-2">
                {alerts.slice(0, 3).map(alert => (
                  <div 
                    key={alert.id}
                    className="flex items-center gap-2 bg-white px-3 py-1 rounded-full text-sm border"
                  >
                    <span className={alert.threshold_percent >= 100 ? "text-red-600" : "text-yellow-600"}>
                      {alert.category_name}: {alert.threshold_percent}%
                    </span>
                    <button 
                      onClick={() => markAlertRead(alert.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <BellOff className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="budget-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="projected-income" data-testid="budget-tab-projected">Projected Income</TabsTrigger>
          <TabsTrigger value="sales-targets" data-testid="budget-tab-sales-targets">Sales Targets</TabsTrigger>
          <TabsTrigger value="categories" data-testid="budget-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="set-budgets" data-testid="budget-tab-set-budgets">Set Budgets</TabsTrigger>
          <TabsTrigger value="airtime-machine" data-testid="budget-tab-airtime">Airtime Machine</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-500">Total Budget</div>
                <div className="text-2xl font-bold">
                  R {summary?.total_budget?.toLocaleString('en-ZA', { minimumFractionDigits: 2 }) || '0.00'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-500">Total Spent</div>
                <div className="text-2xl font-bold text-orange-600">
                  R {summary?.total_spent?.toLocaleString('en-ZA', { minimumFractionDigits: 2 }) || '0.00'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-500">Remaining</div>
                <div className={`text-2xl font-bold ${(summary?.total_remaining || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  R {summary?.total_remaining?.toLocaleString('en-ZA', { minimumFractionDigits: 2 }) || '0.00'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-500">Overall Usage</div>
                <div className="text-2xl font-bold">
                  {summary?.overall_percent_used || 0}%
                </div>
                <Progress 
                  value={Math.min(summary?.overall_percent_used || 0, 100)} 
                  className="mt-2 h-2"
                />
              </CardContent>
            </Card>
          </div>

          {/* Budget Progress Cards */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Budget by Category</CardTitle>
                <Button variant="outline" size="sm" onClick={checkAlerts}>
                  <Bell className="h-4 w-4 mr-2" />
                  Check Alerts
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!summary?.categories?.length ? (
                <div className="text-center py-8 text-gray-500">
                  <PiggyBank className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No budget categories set up yet.</p>
                  <p className="text-sm">Go to "Categories" tab to create budget categories.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {summary.categories.map((cat) => (
                    <div 
                      key={cat.category_id} 
                      className={`p-4 rounded-lg border ${cat.status === 'over' ? 'border-red-200 bg-red-50' : cat.status === 'warning' ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: cat.color }}
                          />
                          <div>
                            <div className="font-medium">{cat.category_name}</div>
                            <div className="text-sm text-gray-500">
                              {cat.expense_count} expense{cat.expense_count !== 1 ? 's' : ''} this month
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-bold ${cat.status === 'over' ? 'text-red-600' : ''}`}>
                            R {cat.spent_amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} / R {cat.budget_amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </div>
                          <div className={`text-sm ${getStatusColor(cat.status)} px-2 py-0.5 rounded-full inline-block`}>
                            {cat.status === 'over' ? (
                              <span className="flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Over by R {Math.abs(cat.remaining).toFixed(2)}
                              </span>
                            ) : cat.status === 'warning' ? (
                              <span className="flex items-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                {cat.percent_used}% used
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                R {cat.remaining.toFixed(2)} remaining
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all ${getProgressColor(cat.percent_used)}`}
                            style={{ width: `${Math.min(cat.percent_used, 100)}%` }}
                          />
                        </div>
                        {cat.percent_used > 100 && (
                          <div 
                            className="absolute top-0 h-3 bg-red-300 rounded-r-full opacity-50"
                            style={{ 
                              left: '100%', 
                              width: `${Math.min(cat.percent_used - 100, 50)}%`,
                              maxWidth: '50%'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projected Income Tab */}
        <TabsContent value="projected-income">
          {projectionLoading ? (
            <div className="text-center py-12 text-gray-500">Loading projections...</div>
          ) : !projectedIncome?.has_reference_data ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Historical Data Available</h3>
                <p className="text-gray-500">
                  Import last year's sales data to see projected daily income.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2 text-purple-800">
                  <TrendingUp className="h-5 w-5" />
                  <span className="font-medium">
                    Projections based on {projectedIncome.reference_period.month_name} {projectedIncome.reference_period.year} sales data
                  </span>
                </div>
                <p className="text-sm text-purple-600 mt-1">
                  Last year's turnover: R {projectedIncome.reference_period.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </p>
              </div>

              {/* Daily/Weekly/Monthly Projections */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <Target className="h-5 w-5" />
                      <span className="text-sm font-medium">Daily Target</span>
                    </div>
                    <div className="text-2xl font-bold text-green-800">
                      R {projectedIncome.projections.daily.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-green-600 mt-1">
                      Profit: R {projectedIncome.projections.daily.profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-blue-700 mb-2">
                      <BarChart3 className="h-5 w-5" />
                      <span className="text-sm font-medium">Weekly Target</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-800">
                      R {projectedIncome.projections.weekly.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      Profit: R {projectedIncome.projections.weekly.profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-purple-700 mb-2">
                      <TrendingUp className="h-5 w-5" />
                      <span className="text-sm font-medium">Monthly Target</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-800">
                      R {projectedIncome.projections.monthly.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-purple-600 mt-1">
                      Profit: R {projectedIncome.projections.monthly.profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-amber-700 mb-2">
                      <PiggyBank className="h-5 w-5" />
                      <span className="text-sm font-medium">Annual Average</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-800">
                      R {projectedIncome.projections.annual.daily_avg.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-amber-600 mt-1">
                      /day (R {(projectedIncome.projections.annual.turnover / 1000000).toFixed(2)}M/year)
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Current Month Progress */}
              {projectedIncome.current_progress.actual_so_far > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      {projectedIncome.target_period.month_name} {projectedIncome.target_period.year} Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Actual So Far (Day {projectedIncome.target_period.current_day})</div>
                        <div className="text-xl font-bold">
                          R {projectedIncome.current_progress.actual_so_far.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Your Daily Average</div>
                        <div className="text-xl font-bold">
                          R {projectedIncome.current_progress.actual_daily_avg.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500">Expected Remaining ({projectedIncome.target_period.days_remaining} days)</div>
                        <div className="text-xl font-bold">
                          R {projectedIncome.current_progress.expected_remaining.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg ${projectedIncome.current_progress.on_track ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="text-sm text-gray-500">Projected Month End</div>
                        <div className={`text-xl font-bold ${projectedIncome.current_progress.on_track ? 'text-green-700' : 'text-red-700'}`}>
                          R {projectedIncome.current_progress.projected_month_end.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </div>
                        <div className={`text-xs mt-1 flex items-center gap-1 ${projectedIncome.current_progress.on_track ? 'text-green-600' : 'text-red-600'}`}>
                          {projectedIncome.current_progress.on_track ? (
                            <><CheckCircle className="h-3 w-3" /> On Track</>
                          ) : (
                            <><AlertTriangle className="h-3 w-3" /> Below Target</>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Progress vs Last Year Same Month</span>
                        <span>{projectedIncome.current_progress.vs_last_year}%</span>
                      </div>
                      <Progress 
                        value={Math.min(projectedIncome.current_progress.vs_last_year, 100)} 
                        className="h-3"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Last Year's Monthly Breakdown ({projectedIncome.reference_period.year})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Turnover</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">Daily Avg Turnover</TableHead>
                        <TableHead className="text-right">Daily Avg Profit</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectedIncome.monthly_breakdown.map((month) => (
                        <TableRow 
                          key={month.month}
                          className={month.month === selectedMonth ? 'bg-purple-50' : ''}
                        >
                          <TableCell className="font-medium">
                            {month.month_name}
                            {month.month === selectedMonth && (
                              <span className="ml-2 text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
                                Selected
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            R {month.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            R {month.profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            R {month.daily_avg_turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-medium">
                            R {month.daily_avg_profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-gray-500">
                            {month.days_in_month}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Annual Total Row */}
                      <TableRow className="bg-gray-100 font-bold">
                        <TableCell>ANNUAL TOTAL</TableCell>
                        <TableCell className="text-right">
                          R {projectedIncome.projections.annual.turnover.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          R {projectedIncome.projections.annual.profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          R {projectedIncome.projections.annual.daily_avg.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          R {projectedIncome.projections.annual.daily_profit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-gray-500">365</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Budget Categories</CardTitle>
                <Button onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <PiggyBank className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No budget categories yet.</p>
                  <p className="text-sm">Click "Add Category" to create your first budget category.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Color</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Linked Expense Category</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell>
                          <div 
                            className="w-6 h-6 rounded-full border"
                            style={{ backgroundColor: cat.color }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell className="text-gray-500">{cat.description || "-"}</TableCell>
                        <TableCell>
                          {cat.expense_category_id ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <Link2 className="h-3 w-3" />
                              {expenseCategories.find(e => e.id === cat.expense_category_id)?.name || "Linked"}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not linked</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingCategory(cat);
                                setCategoryForm({
                                  name: cat.name,
                                  description: cat.description || "",
                                  color: cat.color,
                                  expense_category_id: cat.expense_category_id || ""
                                });
                                setShowCategoryModal(true);
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteCategory(cat.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Set Budgets Tab */}
        <TabsContent value="set-budgets">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Monthly Budgets - {MONTHS[selectedMonth - 1]} {selectedYear}</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCopyModal(true)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy from Previous Month
                  </Button>
                  <Button onClick={() => setShowBudgetModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Set Budget
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Create budget categories first in the "Categories" tab.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Budget Amount</TableHead>
                      <TableHead className="text-right">Current Spending</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat) => {
                      const budgetData = summary?.categories?.find(c => c.category_id === cat.id);
                      return (
                        <TableRow key={cat.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              <span className="font-medium">{cat.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {budgetData?.budget_amount > 0 
                              ? `R ${budgetData.budget_amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`
                              : <span className="text-gray-400">Not set</span>
                            }
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            R {(budgetData?.spent_amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right">
                            {budgetData?.budget_amount > 0 ? (
                              <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(budgetData.status)}`}>
                                {budgetData.percent_used}%
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setBudgetForm({
                                  category_id: cat.id,
                                  budget_amount: budgetData?.budget_amount?.toString() || ""
                                });
                                setEditingBudget(budgetData);
                                setShowBudgetModal(true);
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Targets Tab */}
        <TabsContent value="sales-targets">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  Sales Targets vs Actual - {MONTHS[selectedMonth - 1]} {selectedYear}
                </CardTitle>
                <Button onClick={async () => {
                  // Refresh categories before opening modal
                  try {
                    const catRes = await axios.get(`${API}/categories-departments`);
                    setProductCategories(catRes.data.filter(c => c.type === 'category'));
                  } catch (error) {
                    console.error("Error refreshing categories:", error);
                  }
                  setSalesTargetForm({ category: "", revenue_target: "", profit_target: "" });
                  setEditingSalesTarget(null);
                  setShowSalesTargetModal(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Set Target
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Totals Summary */}
              {salesTargetsSummary?.totals && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="py-4">
                      <div className="text-xs text-blue-600 font-medium">Revenue Target</div>
                      <div className="text-xl font-bold text-blue-800">
                        R {salesTargetsSummary.totals.revenue_target?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="py-4">
                      <div className="text-xs text-green-600 font-medium">Actual Revenue</div>
                      <div className="text-xl font-bold text-green-800">
                        R {salesTargetsSummary.totals.revenue_actual?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </div>
                      <div className={`text-xs flex items-center gap-1 ${salesTargetsSummary.totals.revenue_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {salesTargetsSummary.totals.revenue_variance >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        R {Math.abs(salesTargetsSummary.totals.revenue_variance)?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} vs target
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="py-4">
                      <div className="text-xs text-purple-600 font-medium">Profit Target</div>
                      <div className="text-xl font-bold text-purple-800">
                        R {salesTargetsSummary.totals.profit_target?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="py-4">
                      <div className="text-xs text-orange-600 font-medium">Actual Profit</div>
                      <div className="text-xl font-bold text-orange-800">
                        R {salesTargetsSummary.totals.profit_actual?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </div>
                      <div className={`text-xs flex items-center gap-1 ${salesTargetsSummary.totals.profit_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {salesTargetsSummary.totals.profit_variance >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        R {Math.abs(salesTargetsSummary.totals.profit_variance)?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} vs target
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Previous Year Comparison */}
              {salesTargetsSummary?.totals && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Year-over-Year Comparison ({salesTargetsSummary.prev_year} vs {salesTargetsSummary.year})
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Revenue {MONTHS[selectedMonth - 1]} {salesTargetsSummary.prev_year}:</span>
                      <span className="ml-2 font-semibold">
                        R {salesTargetsSummary.totals.revenue_prev_year?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Profit {MONTHS[selectedMonth - 1]} {salesTargetsSummary.prev_year}:</span>
                      <span className="ml-2 font-semibold">
                        R {salesTargetsSummary.totals.profit_prev_year?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Revenue Target</TableHead>
                    <TableHead className="text-right">Actual Revenue</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Prev Year</TableHead>
                    <TableHead className="text-right">YoY Change</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesTargetsSummary?.categories?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No sales data or targets for this month.</p>
                        <p className="text-sm">Click "Set Target" to add sales targets by category.</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    salesTargetsSummary?.categories?.map((cat) => (
                      <TableRow key={cat.category} className={cat.has_target ? '' : 'bg-gray-50'}>
                        <TableCell>
                          <div className="font-medium">{cat.category}</div>
                          <div className="text-xs text-gray-500">{cat.transaction_count} transactions</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {cat.revenue_target > 0 
                            ? `R ${cat.revenue_target.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`
                            : <span className="text-gray-400">-</span>
                          }
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          R {cat.revenue_actual.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {cat.revenue_target > 0 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              cat.revenue_variance >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {cat.revenue_variance >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {cat.revenue_variance_percent > 0 ? '+' : ''}{cat.revenue_variance_percent}%
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-gray-600">
                          R {cat.revenue_prev_year.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {cat.revenue_prev_year > 0 ? (
                            <span className={`inline-flex items-center gap-1 text-xs ${
                              cat.revenue_yoy >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {cat.revenue_yoy >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                              {cat.revenue_yoy_percent > 0 ? '+' : ''}{cat.revenue_yoy_percent}%
                            </span>
                          ) : <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              // Refresh categories before opening modal
                              try {
                                const catRes = await axios.get(`${API}/categories-departments`);
                                setProductCategories(catRes.data.filter(c => c.type === 'category'));
                              } catch (error) {
                                console.error("Error refreshing categories:", error);
                              }
                              setSalesTargetForm({
                                category: cat.category,
                                revenue_target: cat.revenue_target?.toString() || "",
                                profit_target: cat.profit_target?.toString() || ""
                              });
                              setEditingSalesTarget(cat);
                              setShowSalesTargetModal(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Profit Breakdown */}
              {salesTargetsSummary?.categories?.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3">Profit Breakdown by Category</h4>
                  <div className="space-y-2">
                    {salesTargetsSummary.categories.filter(c => c.profit_actual > 0).slice(0, 10).map((cat) => (
                      <div key={cat.category} className="flex items-center gap-3">
                        <div className="w-32 text-sm truncate">{cat.category}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                              {cat.profit_target > 0 && (
                                <div 
                                  className="absolute h-full bg-blue-200"
                                  style={{ width: `${Math.min((cat.profit_target / (salesTargetsSummary.totals.profit_target || 1)) * 100, 100)}%` }}
                                />
                              )}
                              <div 
                                className={`absolute h-full ${cat.profit_actual >= cat.profit_target ? 'bg-green-500' : 'bg-orange-500'}`}
                                style={{ width: `${Math.min((cat.profit_actual / (salesTargetsSummary.totals.profit_actual || 1)) * 100, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-mono w-24 text-right">
                              R {cat.profit_actual.toLocaleString('en-ZA', { minimumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Airtime Machine Tab */}
        <TabsContent value="airtime-machine">
          <div className="space-y-6">

            {/* Alert Banners */}
            {airtimeBalance?.alert_level === "critical" && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-500 rounded-lg" data-testid="airtime-critical-banner">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-red-700">CRITICAL: Airtime machine balance is very low!</p>
                  <p className="text-sm text-red-600">Balance R {airtimeBalance.balance.toFixed(2)} is below the critical threshold of R {airtimeBalance.critical_threshold?.toFixed(2)}. Top up immediately.</p>
                </div>
              </div>
            )}
            {airtimeBalance?.alert_level === "warning" && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="airtime-warning-banner">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-amber-700">WARNING: Airtime machine balance is running low</p>
                  <p className="text-sm text-amber-600">Balance R {airtimeBalance.balance.toFixed(2)} is below the warning threshold of R {airtimeBalance.warning_threshold?.toFixed(2)}. Consider topping up soon.</p>
                </div>
              </div>
            )}

            {/* Balance Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className={`border-2 ${
                airtimeBalance?.alert_level === "critical" ? "border-red-500" :
                airtimeBalance?.alert_level === "warning" ? "border-amber-400" :
                "border-green-400"
              }`}>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500 mb-1">Current Balance</p>
                  <p data-testid="airtime-balance" className={`text-3xl font-bold ${
                    airtimeBalance?.alert_level === "critical" ? "text-red-600" :
                    airtimeBalance?.alert_level === "warning" ? "text-amber-600" :
                    "text-green-600"
                  }`}>
                    {airtimeLoading ? "..." : `R ${(airtimeBalance?.balance ?? 0).toFixed(2)}`}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Warning &lt; R {airtimeBalance?.warning_threshold ?? 500} · Critical &lt; R {airtimeBalance?.critical_threshold ?? 100}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500 mb-1">Total Loaded</p>
                  <p className="text-2xl font-semibold text-blue-600">
                    R {(airtimeBalance?.total_topups ?? 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500 mb-1">Total Adjustments</p>
                  <p className={`text-2xl font-semibold ${(airtimeBalance?.total_adjustments ?? 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {(airtimeBalance?.total_adjustments ?? 0) >= 0 ? "+" : ""}R {(airtimeBalance?.total_adjustments ?? 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500 mb-1">Total Services Sold</p>
                  <p className="text-2xl font-semibold text-orange-600">
                    R {(airtimeBalance?.total_sales_deducted ?? 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">

                {/* Add Top-Up */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Add Top-Up Payment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Amount (R) *</Label>
                      <Input
                        data-testid="airtime-topup-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 5000"
                        value={topupAmount}
                        onChange={e => setTopupAmount(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        data-testid="airtime-topup-date"
                        type="date"
                        value={topupDate}
                        onChange={e => setTopupDate(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Note (optional)</Label>
                      <Input
                        placeholder="e.g. EFT to Blue Label"
                        value={topupNote}
                        onChange={e => setTopupNote(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      data-testid="airtime-topup-submit"
                      onClick={handleAddTopup}
                      disabled={addingTopup}
                      className="w-full"
                    >
                      {addingTopup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Record Top-Up
                    </Button>
                  </CardContent>
                </Card>

                {/* Balance Adjustment */}
                <Card className="border-orange-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-orange-600">±</span> Balance Adjustment
                    </CardTitle>
                    <p className="text-xs text-gray-500">Increase or decrease balance for corrections/reconciliation</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Amount (R) *</Label>
                      <Input
                        data-testid="airtime-adjustment-amount"
                        type="number"
                        step="0.01"
                        placeholder="e.g. -500 to decrease, 200 to increase"
                        value={adjustmentAmount}
                        onChange={e => setAdjustmentAmount(e.target.value)}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-400 mt-1">Use negative for decrease (e.g. -500)</p>
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        data-testid="airtime-adjustment-date"
                        type="date"
                        value={adjustmentDate}
                        onChange={e => setAdjustmentDate(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Reason *</Label>
                      <Input
                        data-testid="airtime-adjustment-reason"
                        placeholder="e.g. Reconciliation, Correction, Machine error"
                        value={adjustmentReason}
                        onChange={e => setAdjustmentReason(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      data-testid="airtime-adjustment-submit"
                      onClick={handleAddAdjustment}
                      disabled={addingAdjustment}
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      {addingAdjustment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Record Adjustment
                    </Button>
                  </CardContent>
                </Card>

                {/* Alert Threshold Settings */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Alert Thresholds</CardTitle>
                    <p className="text-xs text-gray-500">Set the balance levels that trigger in-app warnings and email alerts.</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Enable Email Alerts</Label>
                      <button
                        onClick={() => setAirtimeSettings(p => ({...p, alerts_enabled: !p.alerts_enabled}))}
                        className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${airtimeSettings.alerts_enabled ? "bg-green-500" : "bg-gray-300"}`}
                        data-testid="airtime-alerts-toggle"
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${airtimeSettings.alerts_enabled ? "translate-x-5" : "translate-x-1"}`} />
                      </button>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />
                        Warning Threshold (R)
                      </Label>
                      <Input
                        data-testid="airtime-warning-threshold"
                        type="number"
                        min="0"
                        value={airtimeSettings.warning_threshold}
                        onChange={e => setAirtimeSettings(p => ({...p, warning_threshold: parseFloat(e.target.value) || 0}))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                        Critical Threshold (R)
                      </Label>
                      <Input
                        data-testid="airtime-critical-threshold"
                        type="number"
                        min="0"
                        value={airtimeSettings.critical_threshold}
                        onChange={e => setAirtimeSettings(p => ({...p, critical_threshold: parseFloat(e.target.value) || 0}))}
                        className="mt-1"
                      />
                    </div>
                    <Button onClick={saveAirtimeSettings} disabled={savingAirtimeSettings} variant="outline" className="w-full" data-testid="save-airtime-settings">
                      {savingAirtimeSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Thresholds
                    </Button>
                    <Button 
                      onClick={handleTestAlert} 
                      disabled={testingAlert} 
                      variant="outline" 
                      className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid="test-airtime-alert"
                    >
                      {testingAlert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Test Email Alert
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      Alerts are checked automatically every 4 hours (6am, 10am, 2pm, 6pm)
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Top-Up History */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top-Up History</CardTitle>
                </CardHeader>
                <CardContent>
                  {airtimeLoading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                  ) : !airtimeBalance?.topups?.length ? (
                    <p className="text-sm text-gray-400">No top-ups recorded yet</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto space-y-2">
                      {airtimeBalance.topups.map(t => (
                        <div key={t.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <div>
                            <span className="font-semibold text-green-700">+R {t.amount.toFixed(2)}</span>
                            <span className="text-gray-400 mx-2">·</span>
                            <span className="text-gray-600">{t.date}</span>
                            {t.note && <span className="text-gray-400 ml-2">— {t.note}</span>}
                          </div>
                          <button
                            onClick={() => handleDeleteTopup(t.id)}
                            className="text-red-400 hover:text-red-600 ml-2"
                            data-testid={`delete-topup-${t.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Adjustment History */}
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-orange-600">±</span> Adjustment History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {airtimeLoading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                  ) : !airtimeBalance?.adjustments?.length ? (
                    <p className="text-sm text-gray-400">No adjustments recorded yet</p>
                  ) : (
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {airtimeBalance.adjustments.map(a => (
                        <div key={a.id} className="flex items-center justify-between p-2 bg-orange-50 rounded text-sm">
                          <div>
                            <span className={`font-semibold ${a.amount >= 0 ? "text-green-700" : "text-red-600"}`}>
                              {a.amount >= 0 ? "+" : ""}R {a.amount.toFixed(2)}
                            </span>
                            <span className="text-gray-400 mx-2">·</span>
                            <span className="text-gray-600">{a.date}</span>
                            <span className="text-orange-600 ml-2">— {a.reason}</span>
                          </div>
                          <button
                            onClick={() => handleDeleteAdjustment(a.id)}
                            className="text-red-400 hover:text-red-600 ml-2"
                            data-testid={`delete-adjustment-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Service Sales */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Recent Service Sales (auto-deducted)</span>
                  <Button size="sm" variant="outline" onClick={fetchAirtimeBalance}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                </CardTitle>
                <p className="text-xs text-gray-500">Showing last 50 service items sold through the POS (airtime, DSTV, electricity, prepaid, etc.)</p>
              </CardHeader>
              <CardContent>
                {!airtimeBalance?.recent_sales?.length ? (
                  <p className="text-sm text-gray-400">No service sales found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Transaction</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {airtimeBalance.recent_sales.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{s.date}</TableCell>
                          <TableCell className="text-sm text-gray-500">{s.transaction}</TableCell>
                          <TableCell className="text-sm">{s.item}</TableCell>
                          <TableCell className="text-sm text-right text-red-600">-R {s.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

      </Tabs>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Edit Budget Category" : "Add Budget Category"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Utilities, Marketing"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Optional description"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      categoryForm.color === color ? "border-gray-800 scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCategoryForm({ ...categoryForm, color })}
                  />
                ))}
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-gray-400" />
                  <Input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="w-10 h-8 p-0 border-0"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Link to Expense Category</Label>
              <p className="text-xs text-gray-500 mb-1">
                Expenses from the linked category will automatically count against this budget
              </p>
              <Select
                value={categoryForm.expense_category_id || "none"}
                onValueChange={(v) => setCategoryForm({ ...categoryForm, expense_category_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select expense category (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {[...expenseCategories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory}>
              {editingCategory ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Set Budget for {MONTHS[selectedMonth - 1]} {selectedYear}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category *</Label>
              <Select
                value={budgetForm.category_id}
                onValueChange={(v) => setBudgetForm({ ...budgetForm, category_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Budget Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                <Input
                  type="number"
                  step="0.01"
                  value={budgetForm.budget_amount}
                  onChange={(e) => setBudgetForm({ ...budgetForm, budget_amount: e.target.value })}
                  placeholder="0.00"
                  className="pl-8"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBudget}>
              Save Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Budgets Modal */}
      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Budgets from Previous Month</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Month</Label>
                <div className="flex gap-2 mt-1">
                  <Select
                    value={copyForm.from_month.toString()}
                    onValueChange={(v) => setCopyForm({ ...copyForm, from_month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month, idx) => (
                        <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={copyForm.from_year.toString()}
                    onValueChange={(v) => setCopyForm({ ...copyForm, from_year: parseInt(v) })}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>To Month</Label>
                <div className="flex gap-2 mt-1">
                  <Select
                    value={copyForm.to_month.toString()}
                    onValueChange={(v) => setCopyForm({ ...copyForm, to_month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month, idx) => (
                        <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={copyForm.to_year.toString()}
                    onValueChange={(v) => setCopyForm({ ...copyForm, to_year: parseInt(v) })}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500">
              This will copy all budget amounts from {MONTHS[copyForm.from_month - 1]} {copyForm.from_year} to {MONTHS[copyForm.to_month - 1]} {copyForm.to_year}.
              Existing budgets in the target month will not be overwritten.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCopyBudgets}>
              <Copy className="h-4 w-4 mr-2" />
              Copy Budgets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Target Modal */}
      <Dialog open={showSalesTargetModal} onOpenChange={setShowSalesTargetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSalesTarget ? `Edit Sales Target - ${editingSalesTarget.category}` : "Set Sales Target"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingSalesTarget && (
              <div>
                <Label>Category *</Label>
                <Select
                  value={salesTargetForm.category}
                  onValueChange={(v) => setSalesTargetForm({ ...salesTargetForm, category: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...productCategories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                      <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                    {/* Also show categories from current summary that might not be in productCategories */}
                    {salesTargetsSummary?.categories
                      ?.filter(c => !productCategories.find(pc => pc.name === c.category))
                      .sort((a, b) => (a.category || '').localeCompare(b.category || ''))
                      .map(c => (
                        <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Revenue Target</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                <Input
                  type="number"
                  step="0.01"
                  value={salesTargetForm.revenue_target}
                  onChange={(e) => setSalesTargetForm({ ...salesTargetForm, revenue_target: e.target.value })}
                  placeholder="0.00"
                  className="pl-8"
                />
              </div>
              {editingSalesTarget && (
                <p className="text-xs text-gray-500 mt-1">
                  Current actual: R {editingSalesTarget.revenue_actual?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            
            <div>
              <Label>Profit Target</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                <Input
                  type="number"
                  step="0.01"
                  value={salesTargetForm.profit_target}
                  onChange={(e) => setSalesTargetForm({ ...salesTargetForm, profit_target: e.target.value })}
                  placeholder="0.00"
                  className="pl-8"
                />
              </div>
              {editingSalesTarget && (
                <p className="text-xs text-gray-500 mt-1">
                  Current actual: R {editingSalesTarget.profit_actual?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            
            {editingSalesTarget && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <div className="font-medium mb-2">Previous Year ({selectedYear - 1}) Comparison:</div>
                <div className="grid grid-cols-2 gap-2 text-gray-600">
                  <div>Revenue: R {editingSalesTarget.revenue_prev_year?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</div>
                  <div>Profit: R {editingSalesTarget.profit_prev_year?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalesTargetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSalesTarget}>
              <Target className="h-4 w-4 mr-2" />
              Save Target
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Budget;
